import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Baby, ChevronRight, AlertTriangle, Clock, Mail } from "lucide-react";
import { PregnancyReminderDialog } from "@/components/PregnancyReminderDialog";

export const PregnancyAlertsCard = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [remind, setRemind] = useState(null);
  useEffect(() => {
    api.get("/patients/pregnancy-alerts").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  const totOverdue = items.reduce((n, i) => n + i.overdue.length, 0);
  return (
    <Card className="mt-4 md:mt-6 p-3 md:p-5 rounded-xl border-rose-200 shadow-sm bg-white"
          data-testid="dashboard-pregnancy-alerts">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 flex items-center justify-center"><Baby className="h-5 w-5" /></div>
          <div>
            <h3 className="font-heading text-base md:text-lg font-bold text-slate-900">Gestanti da richiamare</h3>
            <p className="text-xs text-slate-500">{items.length} pazient{items.length === 1 ? "e" : "i"} con controlli in ritardo o da eseguire ora{totOverdue > 0 ? ` · ${totOverdue} in ritardo` : ""}</p>
          </div>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((p) => (
          <li key={p.patient_fiscal_code} className="py-2.5 flex items-start gap-3 flex-wrap" data-testid={`pregnancy-alert-${p.patient_fiscal_code}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-slate-900 text-sm">
                {p.patient_first_name} {p.patient_last_name}
                <span className="ml-2 text-xs font-normal text-rose-700">{p.estimated_week}ª sett. · DPP {new Date(p.edd).toLocaleDateString("it-IT")}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {p.overdue.map((l) => (
                  <span key={l} className="inline-flex items-center gap-1 text-[11px] rounded-full bg-red-100 text-red-800 border border-red-200 px-2 py-0.5">
                    <AlertTriangle className="h-3 w-3" /> {l}
                  </span>
                ))}
                {p.due.map((l) => (
                  <span key={l} className="inline-flex items-center gap-1 text-[11px] rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5">
                    <Clock className="h-3 w-3" /> {l}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" className="h-8 bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                      data-testid={`pregnancy-alert-remind-${p.patient_fiscal_code}`}
                      onClick={() => setRemind(p)}>
                <Mail className="h-3.5 w-3.5 mr-1" /> Promemoria
              </Button>
              <Button size="sm" variant="outline" className="h-8 bg-white"
                      data-testid={`pregnancy-alert-open-${p.patient_fiscal_code}`}
                      onClick={() => navigate(`/cartella-paziente/${encodeURIComponent(p.patient_fiscal_code)}`)}>
                Cartella <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {remind && (
        <PregnancyReminderDialog open={!!remind} onOpenChange={(o) => !o && setRemind(null)}
                                 fiscalCode={remind.patient_fiscal_code}
                                 patientName={`${remind.patient_first_name} ${remind.patient_last_name}`.trim()} />
      )}
    </Card>
  );
};
